import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useLocation } from 'wouter'
import { User, CounselorProfile } from '@/types'
import { API_ENDPOINTS } from '@/utils/constants'
import { saveTokens, clearTokens } from '@/utils/tokenService'
import { goOffline } from '@/services/presence'
import { webSocketService } from '@/services/websocket'

interface AuthState {
  user: User | null
  counselorProfile: CounselorProfile | null
  profilePictureUrl: string | null
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  isRefreshingToken: boolean
}

interface AuthContextType extends AuthState {
  // Email authentication (active)
  login: (email: string, otp: string) => Promise<boolean>
  // Phone authentication (commented for future use)
  // login: (phoneNumber: string, otp: string) => Promise<boolean>
  logout: () => void
  refreshToken: () => Promise<boolean>
  updateProfile: (profile: Partial<CounselorProfile>) => void
  isAgencyAdmin: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [, setLocation] = useLocation()
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    counselorProfile: null,
    profilePictureUrl: null,
    isAuthenticated: false,
    isLoading: true,
    token: null,
    isRefreshingToken: false
  })


  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (token) {
          // Fetch counselor profile first to validate token and get profile data
          const profileResponse = await fetch(API_ENDPOINTS.PROFILE.COUNSELOR.ME, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })

          if (profileResponse.ok) {
            const profileResponseData = await profileResponse.json()
            const counselorProfile = profileResponseData.data || profileResponseData
            
            // Extract user data from counselor profile or create user object with fallbacks
            const userData: User = {
              id: counselorProfile.userId || counselorProfile.id || '',
              name: counselorProfile.firstName && counselorProfile.lastName 
                ? `${counselorProfile.firstName} ${counselorProfile.lastName}`
                : counselorProfile.name || 'Counselor',
              email: counselorProfile.email || '',
              phoneNumber: counselorProfile.phoneNumber || '',
              userType: 'counselor' as const,
              isActive: true,
              isVerified: true,
              createdAt: counselorProfile.createdAt || new Date().toISOString(),
              updatedAt: counselorProfile.updatedAt || new Date().toISOString()
            }
            
            // Use profile picture URL directly from API response
            const profilePictureUrl = counselorProfile?.profilePicture || null

            setAuthState({
              user: userData,
              counselorProfile,
              profilePictureUrl,
              isAuthenticated: true,
              isLoading: false,
              token,
              isRefreshingToken: false
            })
          } else {
            console.error('Profile fetch failed during auth initialization:', {
              status: profileResponse.status,
              statusText: profileResponse.statusText
            })
            // Token is invalid, remove it
            localStorage.removeItem('auth_token')
            setAuthState(prev => ({
              ...prev,
              isLoading: false
            }))
          }
        } else {
          setAuthState(prev => ({
            ...prev,
            isLoading: false
          }))
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        localStorage.removeItem('auth_token')
        setAuthState(prev => ({
          ...prev,
          isLoading: false
        }))
      }
    }

    initializeAuth()
  }, [])

  // Update profile picture URL when counselorProfile changes
  useEffect(() => {
    setAuthState(prev => ({ 
      ...prev, 
      profilePictureUrl: prev.counselorProfile?.profilePicture || null 
    }))
  }, [authState.counselorProfile?.profilePicture])

  // Re-entrancy guard for logout flows to avoid recursion via tokensCleared event
  const isLoggingOutRef = React.useRef(false)

  // Listen for token refresh events
  useEffect(() => {
    const handleTokenRefresh = (event: CustomEvent) => {
      setAuthState(prev => ({ ...prev, isRefreshingToken: event.detail.isRefreshing }))
    }

    const handleTokenCleared = () => {
      // Avoid recursion/stack overflow: do not call logout() from here
      if (authState.isRefreshingToken || isLoggingOutRef.current) return
      try {
        isLoggingOutRef.current = true
        // Best-effort offline and disconnect
        const counselorId = authState.user?.id
        if (counselorId) {
          void goOffline(counselorId).catch(() => {})
        }
        try { if (webSocketService.isConnected()) { webSocketService.disconnect() } } catch {}
        setAuthState({
          user: null,
          counselorProfile: null,
          profilePictureUrl: null,
          isAuthenticated: false,
          isLoading: false,
          token: null,
          isRefreshingToken: false
        })
      } finally {
        isLoggingOutRef.current = false
      }
    }

    window.addEventListener("tokenRefreshing" as any, handleTokenRefresh)
    window.addEventListener("tokensCleared" as any, handleTokenCleared)

    return () => {
      window.removeEventListener("tokenRefreshing" as any, handleTokenRefresh)
      window.removeEventListener("tokensCleared" as any, handleTokenCleared)
    }
  }, [authState.isRefreshingToken])

  // Email authentication (active)
  const login = async (email: string, otp: string): Promise<boolean> => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.VERIFY_EMAIL_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.toLowerCase().trim(), otp })
      })

      if (response.ok) {
        const responseData = await response.json()
        const data = responseData.data || responseData

        // Handle new user case (shouldn't happen for counselors, but add safety)
        if (data.isNewUser && data.registrationToken) {
          throw new Error('New user registration not supported for counselors. Please contact admin.')
        }

        const { user, accessToken, refreshToken } = data

        // Allow counselors and agency admins to access this dashboard
        const allowedUserTypes = ['counselor', 'agency_admin']
        if (!allowedUserTypes.includes(user.userType?.trim())) {
          throw new Error('Access denied. Only counselors and agency admins can access this dashboard.')
        }

        // Check if user account is active
        if (!user.isActive) {
          console.error('Access denied. Account is inactive:', { userId: user.id, email: user.email })
          throw new Error('Account is inactive. Please contact support to activate your account.')
        }

        // Save both access and refresh tokens
        saveTokens({
          accessToken,
          refreshToken: refreshToken || accessToken // Fallback to access token if no refresh token
        })

        // Fetch counselor profile
        const profileResponse = await fetch(API_ENDPOINTS.PROFILE.COUNSELOR.ME, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        })

        let counselorProfile = null
        let profilePictureUrl = null
        let userData = user // Use the user data from login response as default

        if (profileResponse.ok) {
          const profileResponseData = await profileResponse.json()
          const profileData = profileResponseData.data || profileResponseData
          counselorProfile = profileData

          // Create consistent user data structure by merging user data with profile data
          userData = {
            id: user.id,
            name: user.name || (profileData.firstName && profileData.lastName
              ? `${profileData.firstName} ${profileData.lastName}`
              : user.name || 'Counselor'),
            email: user.email || profileData.email || '',
            phoneNumber: user.phoneNumber || profileData.phoneNumber || '',
            userType: user.userType || 'counselor',
            isActive: user.isActive !== undefined ? user.isActive : true,
            isVerified: user.isVerified !== undefined ? user.isVerified : true,
            createdAt: user.createdAt || new Date().toISOString(),
            updatedAt: user.updatedAt || new Date().toISOString()
          }

          // Use profile picture URL directly from API response
          profilePictureUrl = counselorProfile?.profilePicture || null
        } else {
          // Don't throw error here, continue without profile for now
        }

        setAuthState({
          user: userData,
          counselorProfile,
          profilePictureUrl,
          isAuthenticated: true,
          isLoading: false,
          token: accessToken,
          isRefreshingToken: false
        })

        return true
      } else {
        const errorResponse = await response.json()
        const errorData = errorResponse.data || errorResponse
        throw new Error(errorData.message || 'Login failed')
      }
    } catch (error) {
      return false
    }
  }

  // Phone authentication (commented for future use)
  /*
  const login = async (phoneNumber: string, otp: string): Promise<boolean> => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.VERIFY_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phoneNumber, otp })
      })

      if (response.ok) {
        const responseData = await response.json()
        const data = responseData.data || responseData
        const { user, accessToken, refreshToken } = data

        // Allow counselors and agency admins to access this dashboard

        const allowedUserTypes = ['counselor', 'agency_admin']
        if (!allowedUserTypes.includes(user.userType?.trim())) {
          throw new Error('Access denied. Only counselors and agency admins can access this dashboard.')
        }

        // Save both access and refresh tokens
        saveTokens({
          accessToken,
          refreshToken: refreshToken || accessToken // Fallback to access token if no refresh token
        })

        // Fetch counselor profile
        const profileResponse = await fetch(API_ENDPOINTS.PROFILE.COUNSELOR.ME, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        })

        let counselorProfile = null
        let profilePictureUrl = null
        let userData = user // Use the user data from login response as default

        if (profileResponse.ok) {
          const profileResponseData = await profileResponse.json()
          const profileData = profileResponseData.data || profileResponseData
          counselorProfile = profileData

          // Create consistent user data structure by merging user data with profile data
          userData = {
            id: user.id,
            name: user.name || (profileData.firstName && profileData.lastName
              ? `${profileData.firstName} ${profileData.lastName}`
              : user.name || 'Counselor'),
            email: user.email || profileData.email || '',
            phoneNumber: user.phoneNumber || profileData.phoneNumber || '',
            userType: user.userType || 'counselor',
            isActive: user.isActive !== undefined ? user.isActive : true,
            isVerified: user.isVerified !== undefined ? user.isVerified : true,
            createdAt: user.createdAt || new Date().toISOString(),
            updatedAt: user.updatedAt || new Date().toISOString()
          }

          // Use profile picture URL directly from API response
          profilePictureUrl = counselorProfile?.profilePicture || null
        } else {
          // Don't throw error here, continue without profile for now
        }

        setAuthState({
          user: userData,
          counselorProfile,
          profilePictureUrl,
          isAuthenticated: true,
          isLoading: false,
          token: accessToken,
          isRefreshingToken: false
        })

        return true
      } else {
        const errorResponse = await response.json()
        const errorData = errorResponse.data || errorResponse
        throw new Error(errorData.message || 'Login failed')
      }
    } catch (error) {
      return false
    }
  }
  */

  const logout = () => {
    if (isLoggingOutRef.current) return
    isLoggingOutRef.current = true
    try {
      const counselorId = authState.user?.id
      if (counselorId) {
        // Best-effort offline mark; ignore errors
        void goOffline(counselorId).catch(() => {})
      }
    } catch {}

    try {
      if (webSocketService.isConnected()) {
        webSocketService.disconnect()
      }
    } catch {}

    // Emit token cleared event (listeners will ignore due to re-entrancy guard)
    clearTokens()
    setAuthState({
      user: null,
      counselorProfile: null,
      profilePictureUrl: null,
      isAuthenticated: false,
      isLoading: false,
      token: null,
      isRefreshingToken: false
    })
    
    // Redirect to home page (login page)
    setLocation('/')
    isLoggingOutRef.current = false
    // reload page
    //  NOTE: This is a hacky fix to let the counselor status be offline
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }

  const refreshToken = async (): Promise<boolean> => {
    try {
      const currentToken = localStorage.getItem('auth_token')
      if (!currentToken) return false

      const response = await fetch(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken: currentToken })
      })

      if (response.ok) {
        const responseData = await response.json()
        const data = responseData.data || responseData
        const { accessToken } = data
        localStorage.setItem('auth_token', accessToken)
        
        setAuthState(prev => ({
          ...prev,
          token: accessToken
        }))
        
        return true
      } else {
        logout()
        return false
      }
    } catch (error) {
      logout()
      return false
    }
  }

  const updateProfile = (profile: Partial<CounselorProfile>) => {
    setAuthState(prev => ({
      ...prev,
      counselorProfile: prev.counselorProfile ? { ...prev.counselorProfile, ...profile } : null
    }))
  }


  const isAgencyAdminCalculated = (authState.user?.userType === 'agency_admin')

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshToken,
    updateProfile,
    isAgencyAdmin: isAgencyAdminCalculated
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}